import type { BillRef } from './bill-ref';

/**
 * The vendors' accounts as GET /v1/payments/ledger (Sales Accounts),
 * /v1/payments/ledger/vendor/:vendorId and /v1/vendor/ledger (the vendor's
 * own) give them. A balance is what is owed to the vendor: credit positive.
 */
export type LedgerEntryKind = 'BILL' | 'TDS' | 'DEDUCTION' | 'PAYMENT' | 'REVERSAL';

export interface LedgerLine {
  /** 'YYYY-MM-DD', the day of the business event. */
  readonly date: string;
  readonly kind: LedgerEntryKind;
  readonly invoiceNumber: string | null;
  /** The bill the line belongs to; it opens at /billing/claims/:claimId/bill. */
  readonly claimId: string | null;
  readonly bill: BillRef | null;
  /** UTR or bank reference of a payment. */
  readonly reference: string | null;
  /** TDS section or payment mode. */
  readonly detail: string | null;
  readonly debit: number;
  readonly credit: number;
  readonly balance: number;
}

export interface VendorLedger {
  readonly from: string;
  readonly to: string;
  readonly vendorId: string;
  readonly vendorName: string | null;
  readonly vendorCode: string | null;
  readonly opening: number;
  readonly lines: readonly LedgerLine[];
  readonly totalDebit: number;
  readonly totalCredit: number;
  readonly closing: number;
}

export interface LedgerAmounts {
  readonly opening: number;
  readonly billed: number;
  readonly tds: number;
  readonly deductions: number;
  readonly paid: number;
  readonly reversed: number;
  readonly closing: number;
}

export interface LedgerSummaryRow extends LedgerAmounts {
  readonly vendorId: string;
  readonly vendorName: string | null;
  readonly vendorCode: string | null;
}

/** Days from the bill date; `to` null is the open-ended last bucket. */
export interface AgeingBucket {
  readonly from: number;
  readonly to: number | null;
}

export interface LedgerSummary {
  readonly from: string;
  readonly to: string;
  readonly vendors: readonly LedgerSummaryRow[];
  readonly totals: LedgerAmounts;
  readonly ageing: {
    readonly asOn: string;
    readonly buckets: readonly AgeingBucket[];
    readonly vendors: ReadonlyArray<{
      readonly vendorId: string;
      readonly vendorName: string | null;
      readonly vendorCode: string | null;
      readonly amounts: readonly number[];
      readonly total: number;
    }>;
    readonly totals: readonly number[];
    readonly total: number;
  };
}

/** The query string of a period; an empty end is left to the server's default. */
export function ledgerQuery(from: string, to: string): string {
  const q = new URLSearchParams();
  if (from) q.set('from', from);
  if (to) q.set('to', to);
  const s = q.toString();
  return s ? `?${s}` : '';
}
